import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedDocument } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface DocumentStore {
  documents: SavedDocument[]
  currentDocument: SavedDocument | null
  
  // 基本操作
  addDocument: (document: SavedDocument) => void
  addBatchDocuments: (documents: SavedDocument[]) => void
  updateDocument: (id: string, updates: Partial<SavedDocument>) => void
  removeDocument: (id: string) => void
  getDocument: (id: string) => SavedDocument | undefined
  
  // 現在の文献
  setCurrentDocument: (document: SavedDocument | null) => void
  clearCurrentDocument: () => void
  
  // 検索・フィルター
  searchDocuments: (query: string) => SavedDocument[]
  getDocumentsByType: (type: string) => SavedDocument[]
  getDocumentsByWorld: (worldId: string) => SavedDocument[]
  getDocumentsByCharacter: (characterId: string) => SavedDocument[]
  
  // バッチ操作
  importDocuments: (documents: SavedDocument[]) => void
  exportDocuments: () => SavedDocument[]
  clearAllDocuments: () => void
  clearDocuments: () => void
  
  // エクスポート・インポート
  exportAsText: () => void
  exportAsJSON: () => void
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  importFromJSON: (file: File) => Promise<void>
  
  // タグ管理
  addTag: (documentId: string, tag: string) => void
  removeTag: (documentId: string, tag: string) => void
  getDocumentsByTag: (tag: string) => SavedDocument[]
  getAllTags: () => string[]
  
  // お気に入り
  toggleFavorite: (documentId: string) => void
  getFavoriteDocuments: () => SavedDocument[]
  
  // フォルダ管理
  moveToFolder: (documentId: string, folder: string) => void
  getDocumentsByFolder: (folder: string) => SavedDocument[]
  getAllFolders: () => string[]
}

export const useDocumentStore = create<DocumentStore>()(
  persist(
    (set, get) => ({
      documents: [],
      currentDocument: null,
      
      // 基本操作
      addDocument: (document) => {
        set((state) => ({
          documents: [document, ...state.documents]
        }))
      },
      
      addBatchDocuments: (batchDocuments) => {
        set((state) => {
          let documents = [...batchDocuments, ...state.documents]
          // 上限チェック（200件まで）
          if (documents.length > 200) {
            documents = documents.slice(0, 200)
          }
          return { documents }
        })
      },
      
      updateDocument: (id, updates) => {
        set((state) => ({
          documents: state.documents.map(doc =>
            doc.id === id
              ? { ...doc, ...updates, updatedAt: new Date().toISOString() }
              : doc
          )
        }))
      },
      
      removeDocument: (id) => {
        set((state) => ({
          documents: state.documents.filter(doc => doc.id !== id),
          currentDocument: state.currentDocument?.id === id ? null : state.currentDocument
        }))
      },
      
      getDocument: (id) => {
        return get().documents.find(doc => doc.id === id)
      },
      
      // 現在の文献
      setCurrentDocument: (document) => {
        set({ currentDocument: document })
      },
      
      clearCurrentDocument: () => {
        set({ currentDocument: null })
      },
      
      // 検索・フィルター
      searchDocuments: (query) => {
        const lowercaseQuery = query.toLowerCase()
        return get().documents.filter(doc => {
          // タイトル検索
          if (doc.title.toLowerCase().includes(lowercaseQuery)) return true
          
          // 文献タイプ検索
          if (doc.result.type.toLowerCase().includes(lowercaseQuery)) return true
          
          // タグ検索
          if (doc.tags?.some(tag => tag.toLowerCase().includes(lowercaseQuery))) return true
          
          // メモ検索
          if (doc.memo?.toLowerCase().includes(lowercaseQuery)) return true
          
          return false
        })
      },
      
      getDocumentsByType: (type) => {
        return get().documents.filter(doc => doc.result.type === type)
      },
      
      getDocumentsByWorld: (worldId) => {
        return get().documents.filter(doc => doc.worldId === worldId)
      },
      
      getDocumentsByCharacter: (characterId) => {
        return get().documents.filter(doc => 
          doc.characterIds?.includes(characterId)
        )
      },
      
      // バッチ操作
      importDocuments: (documents) => {
        set((state) => ({
          documents: [...documents, ...state.documents]
        }))
      },
      
      exportDocuments: () => {
        return get().documents
      },
      
      clearAllDocuments: () => {
        if (confirm('すべての文献を削除してもよろしいですか？この操作は取り消せません。')) {
          set({ documents: [], currentDocument: null })
        }
      },
      
      clearDocuments: () => {
        set({ documents: [], currentDocument: null })
      },
      
      // エクスポート・インポート
      exportAsText: () => {
        const documents = get().documents
        if (documents.length === 0) {
          alert('エクスポートする文献がありません')
          return
        }
        
        let text = '=== 保存済み文献一覧 ===\n\n'
        documents.forEach((doc, index) => {
          text += `【${index + 1}】${doc.title}\n`
          text += `タイプ: ${doc.result.type}\n`
          text += `作成日: ${new Date(doc.createdAt).toLocaleString('ja-JP')}\n`
          if (doc.tags && doc.tags.length > 0) {
            text += `タグ: ${doc.tags.join(', ')}\n`
          }
          if (doc.memo) {
            text += `メモ: ${doc.memo}\n`
          }
          text += '\n--- 内容 ---\n'
          text += JSON.stringify(doc.result, null, 2)
          text += '\n\n' + '='.repeat(50) + '\n\n'
        })
        
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `documents_${new Date().toISOString().split('T')[0]}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },
      
      exportAsJSON: () => {
        const documents = get().documents
        if (documents.length === 0) {
          alert('エクスポートする文献がありません')
          return
        }
        
        const exportData = {
          version: 1,
          exportDate: new Date().toISOString(),
          dataType: 'documents',
          data: documents
        }
        
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `documents_${new Date().toISOString().split('T')[0]}.json`
        a.click()
        URL.revokeObjectURL(url)
      },
      
      exportSingleAsText: (id) => {
        const doc = get().documents.find(d => d.id === id)
        if (!doc) return
        
        let text = `=== ${doc.title} ===\n\n`
        text += `タイプ: ${doc.result.type}\n`
        text += `作成日: ${new Date(doc.createdAt).toLocaleString('ja-JP')}\n`
        if (doc.tags && doc.tags.length > 0) {
          text += `タグ: ${doc.tags.join(', ')}\n`
        }
        if (doc.memo) {
          text += `メモ: ${doc.memo}\n`
        }
        text += '\n--- 内容 ---\n'
        text += JSON.stringify(doc.result, null, 2)
        
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${doc.title.replace(/[^a-zA-Z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/g, '_')}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },
      
      exportSingleAsJSON: (id) => {
        const doc = get().documents.find(d => d.id === id)
        if (!doc) return
        
        const exportData = {
          version: 1,
          exportDate: new Date().toISOString(),
          dataType: 'document',
          data: doc
        }
        
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${doc.title.replace(/[^a-zA-Z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/g, '_')}.json`
        a.click()
        URL.revokeObjectURL(url)
      },
      
      importFromJSON: async (file) => {
        try {
          const text = await file.text()
          const data = JSON.parse(text)
          
          if (data.dataType !== 'documents' && data.dataType !== 'document') {
            alert('無効なファイル形式です')
            return
          }
          
          const documentsToImport = data.dataType === 'documents' ? data.data : [data.data]
          
          set((state) => ({
            documents: [...documentsToImport, ...state.documents].slice(0, 500)
          }))
          
          alert(`${documentsToImport.length}件の文献をインポートしました`)
        } catch (error) {
          console.error('インポートエラー:', error)
          alert('ファイルの読み込みに失敗しました')
        }
      },
      
      // タグ管理
      addTag: (documentId, tag) => {
        set((state) => ({
          documents: state.documents.map(doc =>
            doc.id === documentId
              ? {
                  ...doc,
                  tags: [...(doc.tags || []), tag].filter((t, i, arr) => arr.indexOf(t) === i),
                  updatedAt: new Date().toISOString()
                }
              : doc
          )
        }))
      },
      
      removeTag: (documentId, tag) => {
        set((state) => ({
          documents: state.documents.map(doc =>
            doc.id === documentId
              ? {
                  ...doc,
                  tags: (doc.tags || []).filter(t => t !== tag),
                  updatedAt: new Date().toISOString()
                }
              : doc
          )
        }))
      },
      
      getDocumentsByTag: (tag) => {
        return get().documents.filter(doc => doc.tags?.includes(tag))
      },
      
      getAllTags: () => {
        const tags = new Set<string>()
        get().documents.forEach(doc => {
          doc.tags?.forEach(tag => tags.add(tag))
        })
        return Array.from(tags).sort()
      },
      
      // お気に入り
      toggleFavorite: (documentId) => {
        set((state) => ({
          documents: state.documents.map(doc =>
            doc.id === documentId
              ? {
                  ...doc,
                  favorite: !doc.favorite,
                  updatedAt: new Date().toISOString()
                }
              : doc
          )
        }))
      },
      
      getFavoriteDocuments: () => {
        return get().documents.filter(doc => doc.favorite)
      },
      
      // フォルダ管理
      moveToFolder: (documentId, folder) => {
        set((state) => ({
          documents: state.documents.map(doc =>
            doc.id === documentId
              ? {
                  ...doc,
                  folder,
                  updatedAt: new Date().toISOString()
                }
              : doc
          )
        }))
      },
      
      getDocumentsByFolder: (folder) => {
        return get().documents.filter(doc => doc.folder === folder)
      },
      
      getAllFolders: () => {
        const folders = new Set<string>()
        get().documents.forEach(doc => {
          if (doc.folder) folders.add(doc.folder)
        })
        return Array.from(folders).sort()
      }
    }),
    {
      name: 'document-storage',
      storage: createIndexedDBStorage('document-storage'),
      version: 1,
      partialize: (state) => ({
        documents: state.documents
      })
    }
  )
)